'use client';

import { Carousel as NGGCarousel } from '@ngg/components';
import { cn } from '@ngg/storefront-utils';
import type { Image as TypeImage } from '@ts/image';
import Fullscreen from '@ui/components/Fullscreen/Fullscreen';
import Thumbnails from '@ui/components/PictureView/components/Thumbnails';
import Image from 'next/image';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import NextButton from '../carouselButtons/NextButton';
import PrevButton from '../carouselButtons/PrevButton';
import Paragraph from '../Paragraph/Paragraph';

export interface Props {
  dictionary: Record<string, string>;
  productImages: TypeImage[];
  badges: ReactNode;
  className?: string;
  singleImage: Boolean;
}

const visibleSlides = 1;

const setSlideItem = (
  entry: IntersectionObserverEntry,
  setActiveIndex: (index: number) => void,
) => {
  const target = entry.target as HTMLElement;

  if (entry.intersectionRatio > 0.5) {
    setActiveIndex(Number(target.dataset.index));
  }
};

export default function PictureView({
  dictionary,
  productImages,
  badges,
  className,
  singleImage
}: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    if (!ref.current) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => setSlideItem(entry, setActiveIndex),
      {
        root: ref.current,
        rootMargin: '0px',
        threshold: [0.1, 0.5, 0.9],
      },
    );

    Array.from(ref.current.children).forEach((child) => {
      observer.observe(child);
    });

    return () => observer.disconnect();
  }, [ref]);

  const nextButtonVisibility = useCallback(() => {
    if (!ref.current) return true;
    return activeIndex < ref.current.children.length - visibleSlides;
  }, [ref, activeIndex]);

  const goToItem = useCallback(
    (index: number, smooth = true) => {
      if (index < 0) return;
      if (ref.current && index === ref.current.children.length) return;

      const currentChild = ref.current?.children[index] as HTMLDivElement;
      ref?.current?.scrollTo({
        left: currentChild?.offsetLeft,
        behavior: smooth ? 'smooth' : 'auto',
      });
      setActiveIndex(index);
    },
    [ref, setActiveIndex],
  );

  useEffect(() => {
    goToItem(0, false);
  }, [goToItem, productImages]);

  return (
    <>
      <Fullscreen
        open={isFullscreen}
        active={activeIndex}
        onClose={() => setIsFullscreen(false)}
        images={productImages}
        goToCarouselItem={goToItem}
        singleImage={singleImage}
      />

      <div
        className={cn(
          'md:relative md:flex md:flex-col md:gap-4 lg:flex-row',
          !singleImage && 'lg:pl-[4.15rem]',
          className
        )}>
        {!singleImage && (
          <Thumbnails
            goToItem={goToItem}
            activeIndex={activeIndex}
            images={productImages}
            className="hidden max-h-full max-w-full flex-shrink-0 md:bottom-0 md:left-0 md:top-0 md:flex md:overflow-auto lg:absolute lg:flex-col"
          />
        )}

        <NGGCarousel
          ref={ref}
          PreviousButton={() =>
            PrevButton({
              isVisible: activeIndex > 0,
              onClick: (e) => {
                goToItem(activeIndex - 1, false);
                if (activeIndex - 1 <= 0) {
                  e.currentTarget.blur();
                }
              },
              className: 'left-0 bg-transparent hover:bg-main',
            })
          }
          NextButton={() =>
            NextButton({
              isVisible: nextButtonVisibility(),
              onClick: (e) => {
                goToItem(activeIndex + 1, false);
                if (
                  ref?.current &&
                  activeIndex + 1 >= ref.current.children.length - visibleSlides
                ) {
                  e.currentTarget.blur();
                }
              },
              className: 'right-0 bg-transparent hover:bg-main',
            })
          }
          classNames={{
            wrapper: 'bg-beige w-full',
            slider: 'scrollbar-none',
          }}
          Pagination={() =>
            productImages.length > 1 ? (
              <div className="absolute bottom-0 left-0 right-0 md:hidden">
                <Paragraph className="flex grow justify-center px-2 pb-2 text-xs font-bold leading-relaxed">
                  {`${activeIndex + 1}/${productImages.length}`}
                </Paragraph>
              </div>
            ) : null
          }>
          {/* TODO: Account for environment images of a product. These are not sqauare */}
          {productImages?.map((media, index) => (
            <div
              key={`${media.id}-${index}`}
              className="relative flex aspect-portrait h-full items-center justify-center bg-beige md:aspect-portraitDesktop">
              {badges}
              {/* ONLY IMAGES FOR NOW, ADD SUPPORT FOR OTHER MEDIA TYPES LATER */}
              {media.type === 'image' ? (
                <Image
                  src={media.thumbnail ?? media.src}
                  alt={media.alt}
                  width={850}
                  height={1000}
                  className={cn(
                    'aspect-portrait h-full w-full cursor-zoom-in object-cover mix-blend-multiply md:aspect-portraitDesktop',
                    index === 0 && 'object-contain', // First image should always be a display image, needs this styling
                  )}
                  onClick={() => {
                    setIsFullscreen(true);
                    setActiveIndex(index);
                  }}
                  sizes="(max-width: 768px) 100vw, 50vw"
                />
              ) : null}
              {index === 0 ? (
                <Paragraph
                  className={cn(
                    'text-grey-300 text-xxs md:text-xs',
                    'absolute bottom-0 left-0 hidden max-w-full bg-main md:bottom-3.5 md:left-3.5 md:top-auto md:block',
                    'mr-2 px-1 py-1.5 md:mr-3 md:px-3',
                  )}>
                  {dictionary.imageDisclaimerText}
                </Paragraph>
              ) : null}
            </div>
          ))}
        </NGGCarousel>
      </div>
    </>
  );
}
